// Copyright (c) 2022 by Move Closer

import { AnyObject } from '@movecloser/front-core'
import isPlainObject from 'lodash/isPlainObject'

import { logger } from '../../../support'

import { Identifier, Related, RelatedType } from './data.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
type Enum = Record<string, string | number | symbol>

/**
 * Determine whether the given `value` is type of `Identifier`.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @typeGuard
 */
export const isIdentifier = (value: unknown): value is Identifier => {
  return typeof value === 'number' || (typeof value === 'string')
}

/**
 * Determines whether the given `input` correctly implements the `Related` interface.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @typeGuard
 */
export const isRelated = (input: unknown, allowedTypes?: Enum): input is Related => {
  if (!isPlainObject(input)) {
    return false
  }

  const relatedCandidate: AnyObject = input as AnyObject
  if (
    typeof relatedCandidate.type !== 'string' ||
    relatedCandidate.type.length === 0
  ) {
    return false
  }

  if (typeof allowedTypes !== 'undefined' && !isValidEnumValue(relatedCandidate.type, allowedTypes)) {
    return false
  }

  return typeof relatedCandidate.value !== 'undefined' &&
    (isIdentifier(relatedCandidate.value) || relatedCandidate.value === '###')
}

/**
 * Determines whether the given `input` is Related object of given `type`.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @typeGuard
 */
export const isRelatedOfType = (input: unknown, type: RelatedType, allowedTypes?: Enum): input is Related => {
  if (!isRelated(input, allowedTypes)) {
    return false
  }

  return (input as Related).type !== type
}

/**
 * Determines whether the given `subject` variable is a valid value from the specified `map`.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @typeGuard
 */
export const isValidEnumValue = (subject: Enum[keyof Enum], map: Enum): boolean => {
  const isValid: boolean = Object.values(map).includes(subject)

  if (!isValid) {
    logger(
      `[isValidEnumValue]: Provided (subject) is NOT a valid value of the specified (map)!\n(subject) value: ${subject.toString()}.\n(map) value: ${map}.`,
      'error'
    )
  }

  return isValid
}
